<template>
  <div id="app" :class="lastPathSection" @contextmenu="appContextMenu">
    <div v-if="!isEmbedded" ref="mainLoadingScreen" class="main-loading-screen">
      <img src alt>
      <div class="h-v-centerer">
        <p v-cloak class="waking-neo-text">
          {{ $t('message.WAKING_NEO_UP') }}
        </p>
      </div>
      <div class="logo">
        <img src="/images/logo-full-white.png" alt>
      </div>
    </div>
    <!-- Navigation header -->
    <NavHeader
      v-if="currentPage !== 'login'"
      :page-component="currentComponent"
    />
    <!-- Navigation menu quick buttons -->
    <NavBar
      v-if="!isEmbedded"
      :neo-picture-name="iaPicture"
      :config="pageInConfig"
      :is-menu-shown="menuShown"
      @iaClick="showIA"
      @showMenu="menuShown = !menuShown"
    />
    <!-- Navigation menu drawer -->
    <NavDrawer
      v-if="!isEmbedded"
      :is-menu-shown="menuShown"
      @hide="menuShown = false"
    />
    <FullscreenEditor />
    <!-- Page -->
    <div id="page-content">
      <RouterView v-if="pageInConfig || noLogPage || currentTeam" />
      <!-- Meeting navigator -->
      <MeetingNavigator
        v-if="!isEmbedded && currentTeam && currentTeam.id && $store.state.meeting.isMeetingInProgress"
        ref="meetingNavigator"
      />
      <!-- IA Hints -->
      <assistant-hint v-show="currentTeam && currentTeam.id" ref="hint" />
    </div>
    <AssistantOverlay ref="iaOverlay" :component="currentComponent" @showHelp="showHelp" />
    <HelpOverlay
      ref="iaHelp"
      v-bind="helpProps"
      :steps-groups="helpStepsGroups"
      :displayed="helpDisplayed"
      @hide="hideHelp"
    />
    <JiraIssueReporter v-if="!isEmbedded" />
    <ScreenSaver v-if="!isEmbedded && !pageInConfig" />
    <GwOverlay
      v-if="currentTeam && currentTeam.id && !pageInConfig && !isEmbedded"
      :current-component="currentComponent"
    />
    <!-- Banner displaying remote messages -->
    <AnnouncementsBanner
      v-if="!isEmbedded"
      ref="announcementsBanner"
    />
    <ReleaseNotesHandler v-if="!isEmbedded" />
    <SessionActivityHandler v-if="!isEmbedded" />
    <ChartTooltip />
    <Component
      :is="modal.component"
      v-for="(modal, index) in editionModals"
      :key="index"
      :ref="modal.ref"
    />
    <RatingModal ref="ratingModal" />
    <NewDesignModal v-if="!isEmbedded" />
    <NetworkManager v-if="!isEmbedded" />
  </div>
</template>

<script>
import $ from 'jquery';
import toastr from 'toastr';
// Edition modals
import ActionCreationModal from '@/components/edition-modals/action-creation-modal.vue';
import FiveWhysEditionModal from '@/components/edition-modals/five-whys-edition-modal.vue';
import GwCreationModal from '@/components/edition-modals/gw-creation-modal.vue';
import GwOngoingModal from '@/components/edition-modals/gw-ongoing-modal.vue';
import InfoCreationModal from '@/components/edition-modals/info-creation-modal.vue';
import IshikawaEditionModal from '@/components/edition-modals/ishikawa-edition-modal.vue';
import ProblemReportingModal from '@/components/edition-modals/problem-reporting-modal.vue';
import ProblemSheetModal from '@/components/edition-modals/problem-sheet-modal.vue';
// Other components
import AnnouncementsBanner from '@/components/announcements-banner.vue';
import AssistantHint from '@/components/assistant-hint.vue';
import AssistantOverlay from '@/components/assistant-overlay.vue';
import ChartTooltip from '@/components/chart-tooltip.vue';
import FullscreenEditor from '@/components/fullscreen-editor.vue';
import GwOverlay from '@/components/gw-overlay.vue';
import HelpOverlay from '@/components/help-overlay.vue';
import JiraIssueReporter from '@/components/jira-issue-reporter.vue';
import MeetingNavigator from '@/components/meeting-navigator.vue';
import NavBar from '@/components/nav-bar.vue';
import NavDrawer from '@/components/nav-drawer.vue';
import NavHeader from '@/components/nav-header.vue';
import NetworkManager from '@/components/network-manager.vue';
import NewDesignModal from '@/components/new-design-modal.vue';
import RatingModal from '@/components/rating-modal.vue';
import ReleaseNotesHandler from '@/components/release-notes-handler.vue';
import ScreenSaver from '@/components/screen-saver.vue';
import SessionActivityHandler from '@/components/session-activity-handler.vue';
// Socket utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import mixinMeetingPages from '@/mixins/meeting-pages.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Utils
import { calert, keepIAOnRouteChange, isMobile } from '@/utils/main';
import ErrorCode from '@/utils/classes/ErrorCode';
import IAHelpStepsGroup from '@/utils/classes/IAHelpStepsGroup';
import { globalEventDispatcher, ShizenGlobalEvent } from '@/utils/event-dispatcher/global';
import SPage from '@/utils/classes/SPage';
import * as navUtils from '@/utils/navigation-constants';
// Vuex utils
import { FETCH_MENU_CFG_PAGES, FETCH_LOGO, CHECK_LOGIN, SWITCH_USER_TEAM } from '@/store/action-types';
import { FETCH_NAVIGATION_LIST } from '@/store/modules/navigation/action-types';
import { RESUME_MEETING, PROMPT_JOIN_MEETING } from '@/store/modules/meeting/action-types';
import { SET_MEETING_DATA } from '@/store/modules/meeting/mutation-types';
import { SET_CREDENTIALS, SUSPEND_MESSAGE_UPDATE } from '@/store/modules/socket/mutation-types';
import { OPEN_SOCKET, CLOSE_SOCKET } from '@/store/modules/socket/action-types';
import { SET_SCROLLBAR_WIDTH } from '@/store/modules/device/mutation-types';

export default {

  name: 'App',

  components: {
    // Edition modals
    ActionCreationModal,
    FiveWhysEditionModal,
    GwCreationModal,
    GwOngoingModal,
    InfoCreationModal,
    IshikawaEditionModal,
    ProblemReportingModal,
    ProblemSheetModal,
    // Other components
    AnnouncementsBanner,
    AssistantHint,
    AssistantOverlay,
    ChartTooltip,
    FullscreenEditor,
    GwOverlay,
    HelpOverlay,
    JiraIssueReporter,
    MeetingNavigator,
    NavBar,
    NavDrawer,
    NavHeader,
    NetworkManager,
    NewDesignModal,
    RatingModal,
    ReleaseNotesHandler,
    ScreenSaver,
    SessionActivityHandler,
  },

  mixins: [mixinSocketHandler, mixinMeetingPages],

  props: {
    isEmbedded: Boolean,
  },

  data() {
    return {
      // Full menu is displayed or not
      menuShown: false,
      // Users in this team
      thisTeamUsers: [],
      // Intelligent Assistant picture
      iaPicture: 'happy',
      iaOverlay: null,
      helpDisplayed: false,
      helpStepsGroups: [],
      helpProps: {},
      helpHiddenCallback: null,
      currentComponent: null,
      currentComponentTimeout: null,
      // AI Help
      isOnDemoMode: false,
      // Hint provider
      hintProvider: null,
      hintEventsDelay: 86400000,
      hintEventsLast: null,
    };
  },

  computed: {
    /** Current page name as a string */
    currentPage() {
      return this.$route.path.replace('/', '');
    },
    /** Current page name as a string. Used in configurable-page */
    lastPathSection() {
      const sections = this.$route.path.split('/');
      return sections[sections.length - 1];
    },
    /** Boolean: True if the current page is a config page  */
    pageInConfig() {
      return this.$route.meta.requireConfig === true;
    },
    /** Boolean: Page routes that neither require a classic login, nor a config login */
    noLogPage() {
      return this.$route.meta.noLogPage === true;
    },
    workingDays() {
      return (this.currentTeam && this.currentTeam.workingDays) || [true, true, true, true, true, true, true];
    },
    // Currently logged team
    // TODO: Replace all App.user with state.currentTeam
    user() {
      return this.currentTeam || {};
    },
    /** Array: Edition modals displayed over the main content of the page. */
    editionModals() {
      return !this.currentTeam ? [] : [
        { component: 'ProblemSheetModal', ref: 'pbSheet', qParam: navUtils.pbSheetQueryParam },
        { component: 'GwCreationModal', ref: 'gwCreation', qParam: navUtils.gwCreationQueryParam },
        { component: 'GwOngoingModal', ref: 'gwOngoing', qParam: navUtils.ongoingGwQueryParam },
        { component: 'ProblemReportingModal', ref: 'pbReporting', qParam: navUtils.pbReportingQueryParam },
        { component: 'ActionCreationModal', ref: 'actionCreation', qParam: navUtils.actionCreationQueryParam },
        { component: 'InfoCreationModal', ref: 'infoCreation', qParam: navUtils.infoCreationQueryParam },
        { component: 'IshikawaEditionModal', ref: 'ishikawa', qParam: navUtils.ishikawaModalQueryParam },
        { component: 'FiveWhysEditionModal', ref: 'fiveWhys', qParam: navUtils.fwModalQueryParam },
      ];
    },
  },

  watch: {
    $route(to, from) {
      // Check if an overlay is stil displayed
      if (to.path !== from.path && !keepIAOnRouteChange.includes(to.matched[0].path)) {
        if (this.iaOverlay && this.iaOverlay.displayed) {
          this.iaOverlay.hide();
        }
        if (this.isOnDemoMode) {
          this.$refs.iaHelp.hide();
        }
      }
      this.$nextTick(this.checkCurrentComponent);
    },
    isOnDemoMode(newValue) {
      // Toggle socket update according to demo mode
      this.$store.commit(`socket/${SUSPEND_MESSAGE_UPDATE}`, newValue);
    },
    currentTeam(newVal, oldVal) {
      if (!(oldVal && oldVal.id) && newVal.id) {
        this.hideLoading();
        // User has just logged in
        // Launch Dashboard by default
        if (this.currentPage === '' || this.currentPage.match(/login\/?/)) {
          this.$nextTick(this.pushDefaultHomePage);
        }
        // Get navigation items (NavBar, customPages)
        this.$store.dispatch(`navigation/${FETCH_NAVIGATION_LIST}`);
        this.$store.dispatch(FETCH_MENU_CFG_PAGES);
        this.$store.dispatch(FETCH_LOGO);
        // Send event to route components
        if (
          this.currentComponent
          && typeof this.currentComponent.userLoaded === 'function'
        ) {
          this.currentComponent.userLoaded();
        }
        this.openSocket();
        this.$store.dispatch(`meeting/${RESUME_MEETING}`);
        // Handle login from SSO message
        this.handleUrlQueryMessages();
      }
    },
  },

  mounted() {
    this.hintProvider = this.$refs.hint;
    this.$nextTick(() => {
      this.iaOverlay = this.$refs.iaOverlay;
    });
    this.registerSocketHandlers();
    // Set the device scrollbar width for future usage
    this.$store.commit(`device/${SET_SCROLLBAR_WIDTH}`);
    this.$nextTick(this.checkCurrentComponent);
  },

  created() {
    this.setMomentLocale();
    globalEventDispatcher.register(ShizenGlobalEvent.PUSH_HOME_PAGE, this.pushDefaultHomePage);
    globalEventDispatcher.register(ShizenGlobalEvent.NEW_PROBLEM, this.createNewProblem);
    if (this.$route.meta.noCheckLogin || this.isEmbedded) {
      return;
    }
    if (this.$route.query.ssoLoginError !== undefined) {
      this.$nextTick(this.hideLoading);
      return;
    }
    // If no user connected, try to connect one or redirect to login
    if (!this.currentTeam || !this.currentTeam.id) {
      this.$store.dispatch(CHECK_LOGIN)
        .catch((err) => {
          if (!err.data) {
            // No user in cookies or user couldn't be logged in automatically
            if (this.currentPage !== 'login') {
              this.$router.push('/login');
            }
            this.hideLoading();
          }
        });
    } else {
      this.$nextTick(this.hideLoading);
      this.handleUrlQueryMessages();
    }
  },

  beforeDestroy() {
    clearTimeout(this.currentComponentTimeout);
  },

  methods: {
    /** Display messages from query params if any */
    handleUrlQueryMessages() {
      if (this.currentUser && Number(this.$route.query.ssoLoginSuccess) === 1) {
        const query = Object.assign({}, this.$route.query);
        const msg = this.$t('message.LOGIN_SSO_SUCCESSFUL', this.currentUser);
        if (Number(this.$route.query.ssoUserCreated) === 1) {
          delete query.ssoUserCreated;
        }
        toastr.success(msg, { timeOut: 20000, extendedTimeOut: 20000, progressBar: true });
        delete query.ssoLoginSuccess;
        this.$router.replace({ query });
      }
    },
    /** Check current route and set 'currentComponent' data */
    checkCurrentComponent() {
      const { $route } = this;
      const comp = $route.matched && $route.matched.length > 0
        ? $route.matched[$route.matched.length - 1].instances.default
        : null;

      // Check if there's an edition modal and pick the one above the others form the state
      const { editionModalsZIndexes } = this.$store.state;
      if (Object.keys(editionModalsZIndexes).length) {
        const max = Math.max(...Object.values(editionModalsZIndexes));
        const qParam = Object.keys(editionModalsZIndexes).find(key => editionModalsZIndexes[key] === max) || '';
        const modal = this.editionModals.find(m => m.qParam === qParam);
        if (modal) {
          [this.currentComponent] = this.$refs[modal.ref];
        }
        return;
      }
      if (comp) {
        this.currentComponent = comp;
      } else {
        // On initial app load, route loads asynchronously and isn't reactive
        clearTimeout(this.currentComponentTimeout);
        this.currentComponentTimeout = setTimeout(this.checkCurrentComponent, 50);
      }
    },
    appContextMenu(e) {
      if (isMobile) {
        e.preventDefault();
      }
    },
    setMomentLocale() {
      this.$moment.locale(this.$t('min'));
    },
    hideLoading() {
      const mainLoadingScreen = $(this.$refs.mainLoadingScreen);
      mainLoadingScreen.addClass('hidden');
      setTimeout(() => {
        mainLoadingScreen.css('display', 'none');
      }, 500);
    },
    /** if passZero is true, thismethod will call callbackSuccess even if the server returns 0 */
    post(
      url,
      params,
      callbackSuccess,
      hideMsgs,
      passZero,
      callbackError,
    ) {
      this.$http.post(url, Object.assign({ slean_version: this.getVersion() }, params))
        .then(({ data }) => {
          if (!passZero && data === 0) {
            // Session not set
            if (!hideMsgs) {
              toastr.error(this.$t('message.CONNECTION_ERROR'));
            }
            return;
          }
          if (callbackSuccess) {
            callbackSuccess(data);
          }
        })
        .catch((error) => {
          if (!error.response) {
            console.error(error);
          }
          if (!hideMsgs && ErrorCode.NO_MSG_CODES.indexOf(error.response.data) === -1) {
            toastr.error(this.$t('message.CONNECTION_ERROR'));
          }
          if (callbackError) {
            callbackError(error);
          }
        });
    },
    /** Basically the same as post function */
    get(url, callbackSuccess, hideMsgs, passZero) {
      this.$http
        .get(url, { params: { slean_version: this.getVersion() } })
        .then(({ data }) => {
          if (!passZero && data === 0) {
            // Session not set
            if (!hideMsgs) {
              toastr.error(this.$t('message.CONNECTION_ERROR'));
            }
            return;
          }
          callbackSuccess(data);
        })
        .catch(() => {
          if (!hideMsgs) {
            toastr.error(this.$t('message.CONNECTION_ERROR'));
          }
        });
    },
    /** Returns the version nb as a string */
    getVersion() {
      const version = process.env.VUE_APP_VERSION;
      if (version && /^\d+\.\d+\.\d+$/.test(version)) {
        return version;
      }
      return '';
    },
    /** Make the router push the team's default home page */
    pushDefaultHomePage() {
      if (this.currentTeam) {
        const defaultHomePage = this.currentTeam.defaultHomePage || { pageId: SPage.DASHBOARD.id };
        if (defaultHomePage && defaultHomePage.pageId) {
          const link = this.getPageLink(defaultHomePage);
          this.$router.push(link);
        }
      } else {
        this.$router.push(this.getPageLink({ pageId: SPage.LOGIN.id }));
      }
    },
    /** Create a new problem */
    createNewProblem() {
      this.$router.push(this.getPbReportingLink(0));
    },
    /**
     * ===============
     * Socket handlers
     * ===============
     */
    openSocket() {
      this.$store.commit(`socket/${SET_CREDENTIALS}`, this.currentTeam);
      this.$store.dispatch(`socket/${OPEN_SOCKET}`);
    },
    closeSocket() {
      this.$store.dispatch(`socket/${CLOSE_SOCKET}`);
    },
    registerSocketHandlers() {
      this.handleSocketMessage(SocketMessages.MT_MSGS.STARTED, (socketData) => {
        if (!this.$store.state.meeting.isMeetingInProgress) {
          this.$store.dispatch(`meeting/${PROMPT_JOIN_MEETING}`, { id: socketData.id });
        }
      });
      this.handleSocketMessage(SocketMessages.MT_MSGS.END, (socketData) => {
        if (
          this.$store.state.meeting.isMeetingInProgress
          && Number(socketData.id) === Number(this.$store.state.meeting.meetingData.id)
        ) {
          this.$store.commit(`meeting/${SET_MEETING_DATA}`, { meeting: null });
          toastr.info(this.$t('message.MEETING_REMOTELY_CLOSED'));
          this.pushDefaultHomePage();
        }
      });
      this.handleSocketMessage(SocketMessages.NAV_LIST_CHANGE, () => {
        this.$store.dispatch(`navigation/${FETCH_NAVIGATION_LIST}`);
      });
    },
    /** Refresh data.thisTeamUsers */
    refreshTeamUsers(callback) {
      const prevUsers = this.thisTeamUsers;
      const prevPictures = {};
      if (prevUsers) {
        for (let i = 0, len = prevUsers.length; i < len; i++) {
          prevPictures[String(prevUsers[i].id)] = prevUsers[i].picture;
        }
      }
      this.post('/server/users/get.php', {}, (d) => {
        for (let i = 0, len = d.length; i < len; i++) {
          this.setupUserPicture(d, i, prevPictures);
        }
        this.thisTeamUsers = d;
        if (callback) {
          callback();
        }
      });
    },
    setupUserPicture(users, index, prevPictures) {
      prevPictures = prevPictures || [];
      const user = users[index];
      user.picture = prevPictures[String(user.id)] || '';
      user.noPicture = user.picture === '';
      user.label = `${user.firstName} ${user.lastName}`;
      const finalI = index;
      this.get(`/server/users/getPicture.php?id=${user.id}`, (pict) => {
        users[finalI].noPicture = pict.data === '';
        users[finalI].picture = pict.data;
      });
    },
    /** Display the basic assistant overlay */
    showIA() {
      this.menuShown = false;
      if (this.iaOverlay.displayed || this.isOnDemoMode) {
        this.iaOverlay.hide();
        return;
      }
      if (this.currentComponent && this.currentComponent.beforeShowAI) {
        this.currentComponent.beforeShowAI(() => {
          this.displayIA();
        });
      } else {
        this.displayIA();
      }
    },
    displayIA() {
      const shown = this.iaOverlay.show(this.currentComponent, () => {
        // IA closed
        this.iaPicture = 'happy';
      });
      if (shown) {
        this.iaPicture = 'placeholder';
      }
    },
    /** Display the help overlay */
    showHelp(component) {
      this.helpStepsGroups = [];
      if (component && (component.createHelpSteps || component.createHelpStepsGroups)) {
        this.isOnDemoMode = true;
        this.helpStepsGroups = component.createHelpStepsGroups && component.createHelpStepsGroups();
        if (!this.helpStepsGroups) {
          const helpSteps = component.createHelpSteps && component.createHelpSteps();
          if (helpSteps) {
            this.helpStepsGroups = [
              new IAHelpStepsGroup(this.$t('help.PAGE_HELP'), helpSteps),
            ];
          }
        }
      }
      if (this.helpStepsGroups && this.helpStepsGroups.length) {
        this.helpProps = (component.createHelpProps && component.createHelpProps()) || {};
        this.helpDisplayed = true;
        this.helpHiddenCallback = component.helpHidden;
      }
    },
    hideHelp() {
      this.isOnDemoMode = false;
      this.helpDisplayed = false;
      this.helpStepsGroups = [];
      if (typeof this.helpHiddenCallback === 'function') {
        this.helpHiddenCallback();
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/base.scss";

.main-loading-screen {
  background-color: $color-shizen-blue;
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0px;
  left: 0px;
  transition: opacity 0.5s ease-in-out;
  z-index: 12001;
  text-align: center;

  .waking-neo-text {
    color: white;
  }

  &.hidden { opacity: 0; pointer-events: none; }
  > * {
    height: 33%;
    padding: 30px;
    box-sizing: border-box;
    color: #ffffff;
    font-size: 2em;
    position: relative;
  }
  .logo img {
    height: 50px;
    position: absolute;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
  }
}

@media (max-width: $smartphone-portrait) {
  .socket-indicator img {
    margin-right: 0px;
  }
}

</style>
